import { ChevronRightIcon, EyeIcon } from '@heroicons/react/24/outline';
import { FunctionComponent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facture } from '../../types/EntreprisesRessources';
import { FormationSessionDisplay } from '../FormationSessionDisplay';
import { UserDisplay } from '../UserDisplay';
import { DownloadLink } from '../DownloadLink';

/**
 * Ce composant est un encart du tableau de bord entreprises concernant les factures en attente.
 *
 * @param factures - Tableau de factures en attente.
 */
export const DashboardEntrepriseFactures: FunctionComponent<{ factures: Facture[] }> = ({ factures }) => {
  /**
   * Permet au composant de changer de page.
   */
  const navigate = useNavigate();

  return (
    <div className='mx-auto mt-6 shadow-md rounded-md bg-white'>
      <div className='flex flex-col sm:flex-row items-center justify-between p-5'>
        <h3 className='text-secondary-2 poppins-semibold text-center md:text-left'>Factures en attente</h3>
        <Link to='/entreprise/factures' className='text-sm text-secondary-1/90 flex items-center hover:text-primary'>
          Tout voir
          <ChevronRightIcon className='w-3 h-3' />
        </Link>
      </div>
      {factures.length > 0 ? (
        <table className='table-auto w-full bg-white rounded-b-lg'>
          <thead className='text-sm text-gray-500 text-left border-b'>
            <tr className='w-full h-12 border-gray-300  border-b py-8'>
              <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Numéro</th>
              <th className='py-4 text-secondary-1 pl-2 text-left text-sm tracking-normal leading-4'>Formation</th>
              <th className='py-4 text-secondary-1 pl-2 text-left text-sm tracking-normal leading-4'>Salarié</th>
              <th className='py-4 text-secondary-1 pl-2 text-left text-sm tracking-normal leading-4'>Fichier</th>
              <th className='py-4 text-secondary-1 pl-2 text-left text-sm tracking-normal leading-4'>Avancement</th>
              <th className='py-4 text-secondary-1 px-2 text-center text-sm tracking-normal leading-4'>Voir</th>
            </tr>
          </thead>
          <tbody>
            {factures &&
              factures.map((facture) => (
                <tr
                  key={facture.token}
                  className=' border-gray-100 border-b hover:bg-gray-100'
                  onDoubleClick={() => navigate(`/entreprise/factures/${facture.token}`)}>
                  <td className='pl-4 pr-2 py-3 text-secondary-1/90 text-xs'>{facture.numero}</td>
                  <td className='px-2 py-3'>
                    <FormationSessionDisplay formation={facture.formation} groupe={facture.groupe} />
                  </td>
                  <td className='px-2 py-3'>{facture.user && <UserDisplay user={facture.user} />}</td>
                  <td className='px-2 py-3 text-secondary-1 text-xs'>
                    <DownloadLink fichier={facture.fichier} titre={facture.titre} />
                  </td>
                  <td className='px-2 py-3 text-secondary-1/90 text-xs'>
                    {facture.created_at ? (
                      <div>
                        Reçue le :{' '}
                        {new Intl.DateTimeFormat('default', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour12: false,
                        }).format(new Date(facture.created_at as string))}
                      </div>
                    ) : (
                      ''
                    )}
                    {facture.date_limite_paiement ? (
                      <div>
                        Echéance :{' '}
                        {new Intl.DateTimeFormat('default', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour12: false,
                        }).format(new Date(facture.date_limite_paiement as string))}
                      </div>
                    ) : (
                      ''
                    )}
                    {facture.date_paiement ? (
                      <div>
                        Réglée le :{' '}
                        {new Intl.DateTimeFormat('default', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour12: false,
                        }).format(new Date(facture.date_paiement as string))}
                      </div>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className='px-2 py-3 text-secondary-1/90 text-xs'>
                    <div className='flex justify-center'>
                      <Link to={`/entreprise/factures/${facture.token}`}>{<EyeIcon className='w-5 h-5' />}</Link>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      ) : (
        <p className='p-5 text-center text-sm font-medium text-secondary-1/70'>Aucune facture en attente pour le moment.</p>
      )}
    </div>
  );
};
